import { toast, ToastContainer } from "react-toastify";
import { TextField, Button, CircularProgress } from "@mui/material";
import { useNavigate } from "react-router-dom";
import React, { useState, useEffect } from "react";

export default function BankLogin() {
  const [mobile, setMobile] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [timer, setTimer] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    let interval;
    if (timer > 0) {
      interval = setInterval(() => setTimer((prev) => prev - 1), 1000);
    } else if (timer === 0 && otpSent) {
      setOtpSent(false);
      setOtp("");
      toast.error("OTP expired. Please request a new one.");
    }
    return () => clearInterval(interval);
  }, [timer, otpSent]);

  const formatTime = () => {
    const min = Math.floor(timer / 60);
    const sec = timer % 60;
    return `${min}:${sec < 10 ? "0" : ""}${sec}`;
  };

  const validateMobile = () => {
    if (!/^[6-9]\d{9}$/.test(mobile)) {
      setErrors({ mobile: "Enter valid 10-digit mobile number" });
      return false;
    }
    setErrors({});
    return true;
  };

  const handleSendOtp = async (e) => {
    e.preventDefault();
    if (isSubmitting || !validateMobile()) return;

    setIsSubmitting(true);
    setLoading(true);

    try {
      const response = await fetch(`/api/send-otp`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mobile }),
      });

      if (response.ok) {
        setTimer(90);
        setOtpSent(true);
        toast.success("OTP Sent Successfully");
      } else {
        const data = await response.json();
        toast.error(data.error || "Failed to send OTP");
      }
    } catch {
      toast.error("Network error");
    } finally {
      setLoading(false);
      setIsSubmitting(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (isSubmitting || otp.length < 6) {
      toast.error("Enter valid OTP");
      return;
    }

    setIsSubmitting(true);
    setLoading(true);

    try {
      const response = await fetch(`/api/verify-otp`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mobile, otp }),
      });

      if (response.ok) {
        localStorage.setItem("isLoggedIn", "true");
        const data = await response.json();
        document.cookie = "loginName=" + data.loginName;
        navigate("/Dashboard");
        window.location.reload();
      } else {
        const data = await response.json();
        toast.error(data.error || "Invalid OTP");
      }
    } catch {
      toast.error("Network error");
    } finally {
      setLoading(false);
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-[#0B1C2D] text-white relative overflow-hidden">

      <div className="absolute w-[500px] h-[500px] bg-blue-600/20 rounded-full blur-3xl top-[-100px] left-[-150px]" />
      <div className="absolute w-[400px] h-[400px] bg-teal-400/20 rounded-full blur-3xl bottom-[-120px] right-[-120px]" />

      <div className="w-full lg:w-1/2 flex flex-col justify-center items-center lg:items-start px-6 lg:px-12 py-8 lg:py-0 text-center lg:text-left z-10">
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4">
          Co-Operative Society Bank
        </h1>
        <p className="text-white/70 text-sm sm:text-base max-w-md">
          Community banking reimagined with modern security and seamless digital access.
        </p>
        <div className="mt-6 space-y-1 text-xs sm:text-sm text-white/60">
          <p>✔ RBI Compliant Security</p>
          <p>✔ Encrypted Digital Access</p>
          <p>✔ Trusted by Thousands</p>
        </div>
      </div>

      <div className="w-full lg:w-1/2 flex items-center justify-center px-4 sm:px-6 pb-10 lg:pb-0 z-10">
        <div className="w-full max-w-md bg-white/5 backdrop-blur-2xl border border-white/10 rounded-3xl p-6 sm:p-8 shadow-xl">
          <h2 className="text-xl sm:text-2xl font-semibold mb-1">Secure Login</h2>
          <p className="text-white/50 mb-6 text-xs sm:text-sm">Access your banking dashboard</p>

          <TextField
            label="Mobile Number"
            variant="outlined"
            fullWidth
            value={mobile}
            disabled={otpSent}
            onChange={(e) => setMobile(e.target.value.replace(/\D/g, "").slice(0, 10))}
            error={!!errors.mobile}
            helperText={errors.mobile}
            sx={{ mb: 3, input: { color: "#fff", }, label: { color: "#aaa", }, "& .MuiOutlinedInput-root": { "& fieldset": { borderColor: "rgba(255,255,255,0.3)", }, "&:hover fieldset": { borderColor: "#2dd4bf", }, "&.Mui-focused fieldset": { borderColor: "#2dd4bf", }, }, "& input:-webkit-autofill": { WebkitBoxShadow: "0 0 0 100px transparent inset", WebkitTextFillColor: "#fff", transition: "background-color 5000s ease-in-out 0s", }, }}
          />

          {otpSent && (
            <>
              <TextField
                label="Enter OTP"
                variant="outlined"
                fullWidth
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                sx={{ mb: 2, input: { textAlign: "center", letterSpacing: "0.5em", color: "#fff" } }}
              />
              <p className="text-xs text-yellow-300 mb-3">
                OTP expires in {formatTime()}
              </p>
            </>
          )}

          <Button
            fullWidth
            variant="contained"
            onClick={otpSent ? handleVerifyOtp : handleSendOtp}
            disabled={loading}
            sx={{
              py: 1.5,
              borderRadius: "12px",
              fontWeight: "bold",
              background: otpSent
                ? "linear-gradient(to right, #2dd4bf, #22c55e)"
                : "linear-gradient(to right, #3b82f6, #2dd4bf)",
            }}
          >
            {loading ? <CircularProgress size={24} /> : otpSent ? "Verify & Login" : "Send OTP"}
          </Button>

          <p className="text-center text-xs text-white/40 mt-6">
            256-bit SSL Secure Connection
          </p>
        </div>
      </div>

      <ToastContainer position="top-center" />
    </div>
  );
}
